import React, { useEffect, useRef, useState } from 'react'
import { toDateKey, todayKey } from '../../redux/tasks'
import styles from './SchedulePopover.module.css'

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const addDays = (d: Date, days: number) => {
  const next = new Date(d)
  next.setDate(d.getDate() + days)
  return next
}

const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1)
const daysInMonth = (year: number, month: number) =>
  new Date(year, month + 1, 0).getDate()

const formatMonthYear = (d: Date) =>
  new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(d)

const parseDateKey = (key: string): Date => {
  const [y, m, d] = key.split('-').map((n) => parseInt(n, 10))
  return new Date(y, (m ?? 1) - 1, d ?? 1)
}

const CaretLeft = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M15 6l-6 6 6 6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const CaretRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M9 6l6 6-6 6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

export type SchedulePopoverProps = {
  currentScheduledDate?: string
  onPick: (dateKey: string) => void
  onUnschedule: () => void
  onClose: () => void
  align?: 'left' | 'right'
  unscheduleLabel?: string
}

const SchedulePopover = ({
  currentScheduledDate,
  onPick,
  onUnschedule,
  onClose,
  align = 'right',
  unscheduleLabel = 'Move back to Brain Dump',
}: SchedulePopoverProps) => {
  const [view, setView] = useState<Date>(() =>
    startOfMonth(currentScheduledDate ? parseDateKey(currentScheduledDate) : new Date())
  )
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!ref.current) return
      if (!ref.current.contains(e.target as Node)) onClose()
    }
    window.addEventListener('mousedown', onDoc)
    return () => window.removeEventListener('mousedown', onDoc)
  }, [onClose])

  const year = view.getFullYear()
  const month = view.getMonth()
  const firstDow = new Date(year, month, 1).getDay()
  const total = daysInMonth(year, month)
  const todayK = todayKey()

  const cells: Array<{ key: string; label: string; date?: Date }> = []
  for (let i = 0; i < firstDow; i++) cells.push({ key: `pad-${i}`, label: '' })
  for (let d = 1; d <= total; d++) {
    const date = new Date(year, month, d)
    cells.push({ key: toDateKey(date), label: String(d), date })
  }

  const pickToday = () => onPick(todayKey())
  const pickTomorrow = () => onPick(toDateKey(addDays(new Date(), 1)))
  const pickNextWeek = () => onPick(toDateKey(addDays(new Date(), 7)))

  return (
    <div
      ref={ref}
      className={`${styles.popover} ${align === 'left' ? styles.alignLeft : styles.alignRight}`}
      role="dialog"
      aria-label="Schedule task"
    >
      <div className={styles.quick}>
        <button type="button" className={styles.quickBtn} onClick={pickToday}>
          Today
        </button>
        <button type="button" className={styles.quickBtn} onClick={pickTomorrow}>
          Tomorrow
        </button>
        <button type="button" className={styles.quickBtn} onClick={pickNextWeek}>
          Next week
        </button>
      </div>
      <div className={styles.calHead}>
        <button
          type="button"
          className={styles.calNavBtn}
          onClick={() => setView(new Date(year, month - 1, 1))}
          aria-label="Previous month"
        >
          <CaretLeft />
        </button>
        <h4 className={styles.calTitle}>{formatMonthYear(view)}</h4>
        <button
          type="button"
          className={styles.calNavBtn}
          onClick={() => setView(new Date(year, month + 1, 1))}
          aria-label="Next month"
        >
          <CaretRight />
        </button>
      </div>
      <div className={styles.calGrid}>
        {DOW.map((d) => (
          <div key={d} className={styles.calDow}>
            {d}
          </div>
        ))}
        {cells.map((c) => {
          if (!c.date) {
            return <div key={c.key} className={`${styles.calDay} ${styles.calDayMuted}`} />
          }
          const k = toDateKey(c.date)
          const classes = [styles.calDay]
          if (k === todayK) classes.push(styles.calDayToday)
          if (k === currentScheduledDate) classes.push(styles.calDaySelected)
          return (
            <button
              key={c.key}
              type="button"
              className={classes.join(' ')}
              onClick={() => onPick(k)}
            >
              {c.label}
            </button>
          )
        })}
      </div>
      {currentScheduledDate ? (
        <button type="button" className={styles.unscheduleBtn} onClick={onUnschedule}>
          {unscheduleLabel}
        </button>
      ) : null}
    </div>
  )
}

export default SchedulePopover
